/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react/display-name */
// TODO: Fix unused vars
// TODO: Fix missing display name

import { useMemo } from 'react';
import { AppTabularColumn } from '../../../../components/reports/dtos/app-tabular.dto';
import './scenario-planner-details-table.component.scss';
import { ScenarioPlannerCellProdComponent } from './scenario-planner-cell-prod.component';
import { ScenarioPlannerNumberCellComponent } from './scenario-planner-number-cell.component';
import {
  ScenarioPlannerTableComponentProps,
  ScenarioPlannerTableHelper,
} from './scenario-planner-table.helper';
import { TableScenarioSummaryComponent } from './table-scenario-summary.component';
import { TableSummaryComponent } from './table-summary.component';

const parseColumns = (
  columns: AppTabularColumn[],
  data?: any,
  total?: any,
  visibleColumns?: string[] | undefined,
) => {
  const cellFactory =
    (col: AppTabularColumn, index: number) =>
    ({ row }: any) =>
      col.descriptors === true && row.depth !== 0 ? (
        // Child
        <ScenarioPlannerCellProdComponent data={row.original} col={col} />
      ) : (
        // Parent
        <ScenarioPlannerNumberCellComponent data={row.original} col={col} />
      );
  const getColSize = (col: AppTabularColumn, index: number) =>
    col.descriptors === true ? 450 : 250;
  const footerFactory =
    (col: AppTabularColumn, index: number) => (props: any) => {
      return <>{total?.name}</>;
    };

  return ScenarioPlannerTableHelper.parseColumns(
    columns,
    cellFactory,
    getColSize,
    undefined,
    footerFactory,
  );
};

const parseMeasuresColumns = (
  columns: AppTabularColumn[],
  data: any,
  total?: any,
  visibleColumns?: string[],
) => {
  // Filter measureColumns based on visibility if provided
  const filteredMeasureColumns =
    visibleColumns && data?.measureColumns
      ? data.measureColumns.filter((col: { id: string }) =>
          visibleColumns.includes(col.id),
        )
      : data?.measureColumns || [];

  const cellFactory =
    (col: AppTabularColumn, index: number) => (props: any) => (
      <ScenarioPlannerNumberCellComponent data={props.row.original} col={col} />
    );
  const getColSize = (col: AppTabularColumn, index: number) => 60;
  return ScenarioPlannerTableHelper.parseMeasuresColumns(
    filteredMeasureColumns,
    cellFactory,
    getColSize,
    data,
    total,
  );
};

interface ScenarioPlannerDetailsTableComponentProps
  extends ScenarioPlannerTableComponentProps {
  scenarioSummarySelected: boolean;
  onColumnsUpdate?: (columns: AppTabularColumn[]) => void;
  visibleColumns?: string[];
  hiddenRows?: Set<string>;
  selectedRowsForHiding?: string[];
  onRowSelectionForHiding?: (selectedRows: string[]) => void;
  showOnlyFilteredRows?: string[];
  onClearShowOnlyFiltered?: () => void;
  enableRowSelection?: boolean;
  enableMultiRowSelection?: boolean;
  idField?: string;
  isShowOnlyFilteredMode?: boolean;
}

export const ScenarioPlannerSummaryTableComponent = ({
  height = 400,
  scenarioSummarySelected,
  idField = 'id',
  setInvalidateQuery,
  onRowSelectionChange,
  reportName,
  reportId,
  totalReportName,
  filterSelection,
  enabled = true,
  onColumnsUpdate,
  visibleColumns,
  hiddenRows = new Set(),
  selectedRowsForHiding = [],
  onRowSelectionForHiding,
  showOnlyFilteredRows = [],
  onClearShowOnlyFiltered,
  enableRowSelection = true,
  enableMultiRowSelection = true,
  isShowOnlyFilteredMode = false,
}: ScenarioPlannerDetailsTableComponentProps) => {
  const defaultSortBy = useMemo(() => {
    return [{ id: 'name', desc: false }];
  }, []);

  const parseColumnsWithVisibility = useMemo(
    () => (columns: AppTabularColumn[], data?: any, total?: any) =>
      parseColumns(columns, data, total, visibleColumns),
    [visibleColumns],
  );

  const parseMeasuresColumnsWithVisibility = useMemo(
    () => (columns: AppTabularColumn[], data: any, total?: any) =>
      parseMeasuresColumns(columns, data, total, visibleColumns),
    [visibleColumns],
  );

  // Common props for both table types
  const commonTableProps = {
    className: 'scenario-planner-details-table scenario-summary-table',
    filterSelection,
    reportName,
    reportId,
    totalReportName,
    enabled,
    defaultSortBy,
    height,
    parseColumns: parseColumnsWithVisibility,
    parseMeasuresColumns: parseMeasuresColumnsWithVisibility,
    showTotal: true,
    idField,
    onRowSelectionChange,
    enableRowSelection: enableRowSelection && !isShowOnlyFilteredMode, // DISABLE selection in show-only mode
    enableMultiRowSelection: enableMultiRowSelection && !isShowOnlyFilteredMode, // DISABLE multi-selection in show-only mode
    enableStickyFirstColumn: true,
    setInvalidateQuery,
    onColumnsUpdate,
    visibleColumns,
    // Row hiding functionality props
    hiddenRows,
    selectedRowsForHiding: isShowOnlyFilteredMode ? [] : selectedRowsForHiding, // CLEAR selection in show-only mode
    onRowSelectionForHiding,
    showOnlyFilteredRows,
    onClearShowOnlyFiltered,
    isShowOnlyFilteredMode, // Pass through to child components
  };

  if (scenarioSummarySelected) {
    return (
      // Scenario Summary has different data structure. It displays categories along with Total Kc. It doesn't have a tree data structure.
      <TableScenarioSummaryComponent {...commonTableProps} />
    );
  } else {
    // Summary table has tree data structure. Categories as parent and whatever is the selected product level as children.
    return (
      // Summary table has tree data structure. Categories as parent and whatever is the selected product level as children.
      <TableSummaryComponent
        {...commonTableProps}
        className="scenario-planner-details-table scenario-summary-table grouped-rows"
      />
    );
  }
};
